import { Injectable } from '@angular/core';
import { SpinnerService } from '@core/services/spinner.service';
import { TranslationService } from '@core/services/translation.service';
import { AutomationAPI } from '@core/typings/api/automation.typing';
import { ApplicationForUi, ApplicationFromPaginated } from '@core/typings/application.typing';
import { BudgetDetail, BudgetFundingSource, FundingSourceTypes } from '@core/typings/budget.typing';
import { Automation } from '@core/typings/ui/automation.typing';
import { ApplicationManagerService } from '@features/application-manager/services/application-manager/application-manager.service';
import { ApplicationViewService } from '@features/application-view/application-view.service';
import { AwardResources } from '@features/awards/award.resources';
import { BudgetService } from '@features/budgets/budget.service';
import { ProgramFormsService } from '@features/program-forms/program-forms.service';
import { RuleAutomationService } from '@features/rule-automation/rule-automation.service';
import { WorkflowLevelAutomationService } from '@features/workflow-automation/workflow-level-automation.service';
import { DebounceFactory, PaginationOptions } from '@yourcause/common';
import { I18nService } from '@yourcause/common/i18n';
import { LogService } from '@yourcause/common/logging';
import { ModalFactory } from '@yourcause/common/modals';
import { NotifierService } from '@yourcause/common/notifier';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { BudgetAssignmentsResources } from './budget-assignments.resources';
import { BudgetAssignmentsState } from './budget-assignments.state';
import { BudgetAssignmentSequenceModel, SaveBudgetAssignmentRuleSet, SimpleBudgetAssignmentRuleSet } from './budget-assignments.typing';
import { UpdateBudgetAssignmentModalComponent } from './update-budget-assignment/update-budget-assignment.component';


@AttachYCState(BudgetAssignmentsState)
@Injectable({ providedIn: 'root' })
export class BudgetAssignmentsService extends BaseYCService<BudgetAssignmentsState> {
   constructor (
    private logger: LogService,
    private translationService: TranslationService,
    private workflowLevelAutomationService: WorkflowLevelAutomationService,
    private programFormsService: ProgramFormsService,
    private automationService: RuleAutomationService,
    private budgetAssignmentsResources: BudgetAssignmentsResources,
    private i18n: I18nService,
    private notifier: NotifierService,
    private spinnerService: SpinnerService,
    private applicationManagerService: ApplicationManagerService,
    private budgetService: BudgetService,
    private modalFactory: ModalFactory,
    private awardResources: AwardResources,
    private applicationViewService: ApplicationViewService
  ) {
    super();
  }

  ruleSetDataFactory = DebounceFactory.createSimple((paginationOptions: PaginationOptions<SimpleBudgetAssignmentRuleSet>) => {
    return this.budgetAssignmentsResources.getPaginatedList(paginationOptions)
      .then(d => ({
        success: true,
        data: d
      }));
  });

  historyDataFactory = DebounceFactory.createSimple((paginationOptions: PaginationOptions<SimpleBudgetAssignmentRuleSet>) => {
    return this.budgetAssignmentsResources.getBudgetAssignmentHistory(paginationOptions, null)
      .then(data => ({
        success: true,
        data: {
          ...data,
          records: data.records.map(row => {
            return {
              ...row,
              programName: this.translationService.viewTranslations?.Grant_Program?.[row.programId]?.Name ?? row.programName,
              cycleName: this.translationService.viewTranslations?.Grant_Program_Cycle?.[row.cycleId]?.Name ?? row.cycleName
            };
          })
        }
      }));
  });

  async updateApplicationBudgetFundingSource (
    applicationIds: number[],
    budgetId: number,
    fundingSourceId: number,
    reserveFunds: boolean
  ) {
    try {
      for (const applicationId of applicationIds) {
        await this.budgetAssignmentsResources.updateApplicationBudgetFundingSource(
          applicationId,
          budgetId,
          fundingSourceId,
          reserveFunds
        );
      }
      this.historyDataFactory.reset.emit();
      this.notifier.success(this.i18n.translate('CONFIG:textSuccessfullyUpdatedBudgetFundingSource', {}, 'Successfully updated the budget/funding source of the application'));
    } catch (error) {
      this.logger.error(error);
      this.notifier.error(this.i18n.translate('CONFIG:textErrorUpdatingBudgetFundingSource', {}, 'There was an error updating the budget/funding source of the application'));
    }
  }
  async changeStatus (id: number, status: AutomationAPI.AutomationRuleSetStatus) {
    try {
      await this.budgetAssignmentsResources.changeStatus(id, status);
      this.ruleSetDataFactory.reset.emit();
      this.notifier.success(this.i18n.translate('CONFIG:textSuccessfullySetBudgetRuleStatus', {}, 'Successfully changed the rule\'s status'));
    } catch (e) {
      this.logger.error(e);
      this.notifier.error(this.i18n.translate('CONFIG:textErrorSettingBudgetRuleStatus', {}, 'There was an error changing this rule\'s status'));
    }
  }

  async fetchDetail (id: number) {
    const detail = await this.budgetAssignmentsResources.getDetail(id);
    this.set('detailMap', {
      ...this.get('detailMap'),
      [id]: detail
    });

    return detail;
  }

  async handleCopyRule (id: number) {
    const detail = await this.fetchDetail(id);

    return this.saveRuleSet({
      ...detail,
      budgetAssignmentAutomationRuleSetId: null,
      name: detail.name + ' - Copy',
      rules: detail.rules.map(rule => ({
        ...rule,
        automationRuleSetExpressionId: null
      }))
    });
  }

  async saveRuleSet (detail: SaveBudgetAssignmentRuleSet) {
    let result: SimpleBudgetAssignmentRuleSet;
    try {
      result = await this.budgetAssignmentsResources.saveRuleSet(detail);
      this.notifier.success(this.i18n.translate('CONFIG:textSuccessSaveBudgetRule', {}, 'Successfully saved budget assignment rule'));
    } catch (e) {
      this.notifier.error(this.i18n.translate('CONFIG:textErrorSavingBudgetRule', {}, 'There was an error saving the budget assignment rule'));
      this.logger.error(e);
    }

    return result?.budgetAssignmentAutomationRuleSetId;
  }

  async deleteRuleSet (id: number) {
    try {
      await this.budgetAssignmentsResources.deleteRuleSet(id);
      this.ruleSetDataFactory.reset.emit();
      this.notifier.success(this.i18n.translate('CONFIG:textSuccessDeletingBudgetRule', {}, 'Successfully deleted budget assignment rule'));
    } catch (e) {
      this.notifier.error(this.i18n.translate('CONFIG:textErrorDeletingRule', {}, 'There was an error deleting this budget assignment rule'));
      this.logger.error(e);
    }
  }

  async getRulesByProgramId (programId: number) {
    const result = await this.budgetAssignmentsResources.getPaginatedList({
      filterColumns: [{
        columnName: 'grantProgramId',
        filters: [{
          filterType: 'eq',
          filterValue: programId
        }]
      }],
      orFilterColumns: [],
      returnAll: true,
      retrieveTotalRecordCount: false,
      rowsPerPage: 1000,
      pageNumber: 0,
      sortColumns: [{
        columnName: 'sequence',
        sortAscending: true
      }]
    });

    return result.records;
  }

  async sequenceRules (sequence: BudgetAssignmentSequenceModel) {
    this.spinnerService.startSpinner();
    try {
      await this.budgetAssignmentsResources.sequence(sequence);
      this.notifier.success(this.i18n.translate('CONFIG:notificationSuccessfullySequencedBudgetRules', {}, 'Successfully sequenced budget assignment rules'));
    } catch (e) {
      this.notifier.error(this.i18n.translate('CONFIG:notificationErrorSequencingBudgetAssignments', {}, 'There was an error sequencing budget assignment rules'));
      this.logger.error(e);
    }
    this.spinnerService.stopSpinner();
  }

  async getRuleObjectsByProgramId (programId: number): Promise<Automation.ObjectConfig<any, any>[]> {
    const forms = await this.programFormsService.getProgramForms(programId);
    const defaultForm = forms.find(form => form.isDefault);

    const formObject = await this.automationService.formToRuleObject({
      id: defaultForm.formId,
      name: defaultForm.formName,
      formDefinition: defaultForm.formDefinition
    });
    const workflowObjects = this.workflowLevelAutomationService.getWorkflowObjects(false);

    return [
      ...workflowObjects,
      formObject
    ];
  }

  async getReservedAmountForApplication (
    applicationId: number,
    amountRequested: number
  ) {
    const awards = await this.awardResources.getAwards(applicationId);
    const award = awards.awards.find(_award => {
      return _award.awardType === FundingSourceTypes.DOLLARS;
    });

    // The potential reserved amount would be the award amount minus all of the payments
    const amountToReserve = award ?
      award.amount - award.payments.reduce((acc, payment) => {
        return acc + payment.totalAmount;
      }, 0) :
      amountRequested;

    // do this to prevent negative numbers, in case the payments are higher than the award amount
    return Math.max(0, amountToReserve);
  }


  async updateBudgetModal (
    records: (ApplicationFromPaginated|ApplicationForUi)[],
    appDetail?: ApplicationForUi // if it's just 1 app and we already have the detail
  ) {
    this.spinnerService.startSpinner();
    let budget: BudgetDetail;
    let fundingSource: BudgetFundingSource;
    // we only need to show the current budget if we are reassigning ONE application
    if (records.length === 1) {
      if (!appDetail) {
        appDetail = await this.applicationViewService.getApplicationDetail(
          records[0].applicationId
        );
      }
      if (appDetail.budgetId) {
        budget = await this.budgetService.setBudgetDetail(appDetail.budgetId);
        fundingSource = budget.budgetFundingSources.find(fs => fs.fundingSourceId === appDetail.fundingSourceId);
      }
    }
    const detailsById = await this.applicationManagerService.getBulkApplicationDetailsMap(
      records.map((app) => app.applicationId)
    );

    // calculate the payment amounts for each of the bulk records
    const amounts = await Promise.all(records.map(async (record) => {
      const app = detailsById[record.applicationId];
      // calculate the remaining reservation amount if the record has a cash award
      if (app.hasCashAward) {
        const amountToReserve = await this.getReservedAmountForApplication(
          record.applicationId,
          record.amountRequested
        );

        return amountToReserve;
      }

      // otherwise it's the amount requested
      return record.amountRequested;
    }));

    const amountToFund = amounts.reduce((acc, amount) => {
      return acc + amount;
    }, 0);

    this.spinnerService.stopSpinner();

    // we can assume the program/cycle name from the first one because bulk actions can only happen on apps in the same program/cycle
    const appIds = records.map(app => app.applicationId);
    const viewTranslations = this.translationService.viewTranslations;
    const programName = viewTranslations?.Grant_Program?.[records[0].programId]?.Name ??
      records[0].programName;
    const cycleName = viewTranslations?.Grant_Program_Cycle?.[records[0].grantProgramCycle.id]?.Name ??
      records[0].grantProgramCycle.name;
    const currentBudgetName = budget?.name ??
      this.i18n.translate('BUDGET:textNoBudget', {}, 'No budget');
    const currentFundingSourceName = fundingSource?.fundingSourceName ??
      this.i18n.translate('BUDGET:textNoFundingSource', {}, 'No funding source');

    const result = await this.modalFactory.open(
      UpdateBudgetAssignmentModalComponent, {
      programName,
      programId: records[0].programId,
      cycleName,
      cycleId: records[0].grantProgramCycle.id,
      currentBudgetName,
      currentBudgetId: appDetail?.budgetId,
      currentFundingSourceName,
      currentFundingSourceId: appDetail?.fundingSourceId,
      amountRequested: amountToFund,
      applicationIds: appIds,
      isCurrentlyReserved: appDetail?.reservedFunds
    });

    return result;
  }
}
